import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormLabel,
  InputAdornment,
  Theme,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Axios from "../../Common/api/axiosConfig";
import { useNavigate } from "react-router-dom";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DescriptionIcon from "@mui/icons-material/Description";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkIcon from "@mui/icons-material/Link";
import HomeIcon from "@mui/icons-material/Home";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Save, Image, Clear } from "@mui/icons-material";
import { textFieldStyles } from "../../Components/Common/styles/textfieldStyles";
import { formLabelStyles } from "../../Components/Common/styles/formLabelStyles";
import CountryCodePhoneInput from "../../Components/Common/CountryCodePhoneInput";
import { setActiveStoreId } from "../../utils/storeHelper";

const CreateStore = () => {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [images, setImages] = useState<File[]>([]);

  // Validation Schema
  const validationSchema = Yup.object({
    businessName: Yup.string()
      .required("Business name is required")
      .min(2, "Business name must be at least 2 characters")
      .max(100, "Business name must be less than 100 characters"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(500, "Description must be less than 500 characters"),
    whatsappNumber: Yup.string()
      .required("WhatsApp number is required")
      .test(
        "is-valid-phone",
        "WhatsApp number must be at least 10 digits including country code",
        (value) => {
          if (!value) return false;
          const digits = value.replace(/[^0-9]/g, "");
          return digits.length >= 10 && digits.length <= 16;
        }
      ),
    storeUrl: Yup.string()
      .required("Store URL is required")
      .matches(
        /^[a-z0-9-]+(\.[a-z0-9-]+)*$/i,
        "Store URL can contain lowercase letters, numbers, hyphens, and dots (for domains)"
      )
      .min(2, "Store URL must be at least 2 characters")
      .max(80, "Store URL must be less than 80 characters"),
    address: Yup.string().max(200, "Address must be less than 200 characters"),
    operatingHours: Yup.string().max(
      100,
      "Operating hours must be less than 100 characters"
    ),
    deliveryInfo: Yup.string().max(
      300,
      "Delivery information must be less than 300 characters"
    ),
  });

  const formik = useFormik({
    initialValues: {
      businessName: "",
      description: "",
      whatsappNumber: "",
      storeUrl: "",
      address: "",
      operatingHours: "",
      deliveryInfo: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          setError("Authentication required. Please login again.");
          setOpenSnackbar(true);
          navigate("/login");
          return;
        }

        // Prepare form data (includes images)
        const formData = new FormData();
        formData.append("name", values.businessName);
        formData.append("description", values.description);
        formData.append("number", values.whatsappNumber);
        formData.append("url", values.storeUrl);
        if (values.address) formData.append("address", values.address);
        if (values.operatingHours)
          formData.append("operating_hours", values.operatingHours);
        if (values.deliveryInfo)
          formData.append("delivery", values.deliveryInfo);

        images.forEach((img) => formData.append("image", img));

        const response = await Axios.post("/store", formData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("Store created successfully:", response.data);
        if (response.data?.store?._id) {
          setActiveStoreId(response.data.store._id);
        }
        setSuccess("Store created successfully! Redirecting...");
        setOpenSnackbar(true);

        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } catch (err: any) {
        console.error("Store creation error:", err);
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to create store. Please try again.";
        setError(message);
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError("");
    setSuccess("");
  };

  // Handle Image Upload
  const handleMultipleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter((file) =>
      ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)
    );

    if (validFiles.length !== files.length) {
      setError("Only JPG, PNG, and WEBP images are allowed.");
      setOpenSnackbar(true);
      return;
    }

    setImages((prev) => [...prev, ...validFiles]);

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  // Handle Image Removal
  const handleRemoveImage = (index: number) => {
    const newPreviews = [...imagePreviews];
    const newImages = [...images];
    newPreviews.splice(index, 1);
    newImages.splice(index, 1);
    setImagePreviews(newPreviews);
    setImages(newImages);
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 4, md: 20 } }}>
      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : success ? "success" : "info"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>

      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 3,
          }}
        >
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Create Store
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your Rudrix store
            </Typography>
          </Box>

          <Button
            variant="contained"
            type="submit"
            disabled={loading || !formik.isValid}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save />
              )
            }
            sx={{
              borderRadius: 3,
              mt: 4,
              background: "linear-gradient(135deg, #2196f3, #42a5f5)",
              color: "#fff",
              textTransform: "none",
              px: 3,
              py: 1,
              "&:disabled": {
                background: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            {loading ? "Creating Store..." : "Save Store"}
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Left Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Business Information
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Basic details about your business
                </Typography>

                {/* Business Name */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <StorefrontIcon /> Business Name *
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="businessName"
                    placeholder="Enter business name"
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.businessName &&
                      Boolean(formik.errors.businessName)
                    }
                    helperText={
                      formik.touched.businessName && formik.errors.businessName
                    }
                    sx={textFieldStyles(theme)}
                  />
                </Box>

                {/* Description */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <DescriptionIcon /> Description *
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="description"
                    placeholder="Enter description"
                    multiline
                    rows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.description &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                    sx={textFieldStyles(theme)}
                  />
                </Box>

                {/* WhatsApp */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <WhatsAppIcon /> WhatsApp Number *
                  </FormLabel>
                  <CountryCodePhoneInput
                    name="whatsappNumber"
                    placeholder="98765 43210"
                    value={formik.values.whatsappNumber}
                    onChange={(e: any) => {
                      const val = e?.target?.value !== undefined ? e.target.value : e;
                      formik.setFieldValue("whatsappNumber", val);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.whatsappNumber &&
                      Boolean(formik.errors.whatsappNumber)
                    }
                    helperText={
                      formik.touched.whatsappNumber &&
                      formik.errors.whatsappNumber
                        ? formik.errors.whatsappNumber
                        : "Include country code (e.g. +91 for India)"
                    }
                  />
                </Box>

                {/* Store URL */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <LinkIcon /> Store URL *
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="storeUrl"
                    placeholder="e.g., shop or therudrix.in"
                    value={formik.values.storeUrl}
                    onChange={(e) => {
                      const val = e.target.value.toLowerCase().replace(/^(https?:\/\/)/i, "").replace(/\/$/, "");
                      formik.setFieldValue("storeUrl", val);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.storeUrl && Boolean(formik.errors.storeUrl)
                    }
                    helperText={
                      formik.touched.storeUrl && formik.errors.storeUrl
                        ? formik.errors.storeUrl
                        : formik.values.storeUrl.includes(".")
                        ? `Custom Domain: https://${formik.values.storeUrl}`
                        : `Store URL: https://therudrix.in/${formik.values.storeUrl || "your-store"}`
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            fontWeight: 600,
                            color: formik.values.storeUrl.includes(".") ? "primary.main" : "text.secondary",
                            fontSize: "0.85rem",
                          }}
                        >
                          {formik.values.storeUrl.includes(".") ? "https://" : "therudrix.in/"}
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldStyles(theme)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Additional Details
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Optional information for customers
                </Typography>

                {/* Address */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <HomeIcon /> Business Address
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="address"
                    placeholder="Enter your business address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.address && Boolean(formik.errors.address)
                    }
                    helperText={formik.touched.address && formik.errors.address}
                    sx={textFieldStyles(theme)}
                  />
                </Box>

                {/* Operating Hours */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <AccessTimeIcon /> Operating Hours
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="operatingHours"
                    placeholder="e.g., Mon–Fri 9AM–6PM"
                    value={formik.values.operatingHours}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.operatingHours &&
                      Boolean(formik.errors.operatingHours)
                    }
                    helperText={
                      formik.touched.operatingHours &&
                      formik.errors.operatingHours
                    }
                    sx={textFieldStyles(theme)}
                  />
                </Box>

                {/* Delivery Info */}
                <Box sx={{ mb: 2 }}>
                  <FormLabel sx={formLabelStyles(theme)}>
                    <LocalShippingIcon /> Delivery Information
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    name="deliveryInfo"
                    placeholder="Enter delivery details"
                    multiline
                    rows={3}
                    value={formik.values.deliveryInfo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.deliveryInfo &&
                      Boolean(formik.errors.deliveryInfo)
                    }
                    helperText={
                      formik.touched.deliveryInfo && formik.errors.deliveryInfo
                    }
                    sx={textFieldStyles(theme)}
                  />
                </Box>

                {/* Image Upload */}
                <Box>
                  <FormLabel
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Image fontSize="small" /> Store Images
                  </FormLabel>

                  {imagePreviews.length > 0 && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {imagePreviews.map((preview, index) => (
                        <Grid item xs={6} sm={4} key={index}>
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              paddingTop: "100%",
                              borderRadius: 2,
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                              bgcolor: "#f5f5f5",
                            }}
                          >
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveImage(index)}
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                bgcolor: "rgba(255,255,255,0.9)",
                              }}
                            >
                              <Clear fontSize="small" color="error" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Image />}
                    fullWidth
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                    }}
                  >
                    Upload Images
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      multiple
                      onChange={handleMultipleImageUpload}
                    />
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CreateStore;
